<template>
  <div v-if="screener && !isScreeningArchive">
    <v-tabs
      v-model="currentPricingTab"
      grow
      color="background lighten-4"
      slider-color="primary"
      @click.stop=""
    >
      <v-tab
        v-if="isSinglePricingAvailableOnScreening(screener)"
        href="#single_pricing"
        @click.stop=""
        class="secondary--text"
      >
        {{ $t("screener.monetization.single_pricing.label") }}
      </v-tab>
      <v-tab
        v-if="isGroupPricingAvailableOnScreening(screener)"
        href="#group_pricing"
        @click.stop=""
        class="secondary--text"
      >
        {{ $t("screener.monetization.group_pricing.label") }}
      </v-tab>
      <v-tab
        v-if="isVodPricingAvailableOnScreening(screener)"
        href="#vod_pricing"
        @click.stop=""
        class="secondary--text"
      >
        {{ $t("screener.monetization.vod_pricing.label") }}
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="currentPricingTab">
      <v-form ref="pricingPlanForm">
        <!-- Single Pricing -->
        <v-tab-item
          v-if="isSinglePricingAvailableOnScreening(screener)"
          value="single_pricing"
          class="px-3"
        >
          <v-radio-group v-model="singlePricingPlan" @click.stop="">
            <v-radio
              :value="
                screener.monetization[
                  DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                ][0].id
              "
              color="primary"
              @change="
                $emit(
                  'onSetPricingPlan',
                  screener.monetization[
                    DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                  ][0],
                  screener
                )
              "
            >
              <template v-slot:label>
                <h3 class="secondary--text">
                  {{
                    getCurrencySymbol(
                      screener.monetization[
                        DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                      ][0].currency
                    )
                  }}{{
                    screener.monetization[
                      DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
                    ][0].price
                  }}
                </h3>
              </template></v-radio
            >
          </v-radio-group>
        </v-tab-item>
        <!-- Group Pricing -->
        <v-tab-item
          v-if="isGroupPricingAvailableOnScreening(screener)"
          value="group_pricing"
          class="px-3"
        >
          <v-radio-group v-model="groupPricingPlan" @click.stop="">
            <template
              v-for="groupPlan in screener.monetization[
                DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
              ]"
            >
            <!-- Show only not purchased & active plans -->
              <v-radio
                v-if="
                  !hasGroupTicketPurchased(groupPlan.id) &&
                  checkPurchasingDateIsFuture(groupPlan.purchasing_end_date)
                "
                :value="groupPlan.id"
                :key="groupPlan.id"
                color="primary"
                @change="$emit('onSetPricingPlan', groupPlan)"
                class="pb-1"
              >
                <template v-slot:label>
                  <div>
                    <h3 class="secondary--text">
                      {{ getCurrencySymbol(groupPlan.currency)
                      }}{{ groupPlan.price }}
                    </h3>
                    <h4 class="d-inline">
                      {{ groupPlan.name }}
                    </h4>
                    <span class="title px-1">•</span>
                    <router-link
                      class="blue--text"
                      :to="{
                        name: 'pricing.overview',
                        params: {
                          pricing_id: groupPlan.id
                        }
                      }"
                      >{{ $t("screener.view_detail.title") }}</router-link
                    >
                  </div>
                </template>
              </v-radio>
            </template>
          </v-radio-group>
        </v-tab-item>

        <!-- VOD Pricing -->
        <v-tab-item
          v-if="isVodPricingAvailableOnScreening(screener)"
          value="vod_pricing"
          class="px-3"
        >
          <v-radio-group v-model="vodPricingPlan" @click.stop="">
            <v-radio
              :value="
                screener.monetization[
                  DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
                ][0].id
              "
              color="primary"
              @change="
                $emit(
                  'onSetPricingPlan',
                  screener.monetization[
                    DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
                  ][0],
                  screener
                )
              "
            >
              <template v-slot:label>
                <h3 class='secondary--text'>
                  {{
                    getCurrencySymbol(
                      screener.monetization[
                        DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
                      ][0].currency
                    )
                  }}{{
                    screener.monetization[
                      DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
                    ][0].price
                  }}
                </h3>
              </template></v-radio
            >
          </v-radio-group>
        </v-tab-item>
      </v-form>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { currencySymbol } from "@/mixins/currencySymbol";
import { hasGroupTicketPurchased } from "@/mixins/monetization";
import { isDateFuture, isDatePast } from "@/helpers";

export default {
  name: "PricingHandler",
  data() {
    return {
      currentPricingTab: null,
      singlePricingPlan: null,
      groupPricingPlan: null,
      vodPricingPlan: null,
    };
  },
  props: {
    screener: {
      type: Object,
      default: null
    }
  },
  mixins: [currencySymbol, hasGroupTicketPurchased],
  watch: {
    singlePricingPlan: function(newValue, oldValue) {
      if (newValue) {
        this.groupPricingPlan = null;
      }
    },
    groupPricingPlan: function(newValue, oldValue) {
      if (newValue) {
        this.singlePricingPlan = null;
      }
    }
  },
  computed: {
    ...mapGetters("screening", {
      isSinglePricingAvailableOnScreening:
        "isSinglePricingAvailableOnScreening",
      isGroupPricingAvailableOnScreening: "isGroupPricingAvailableOnScreening",
      isVodPricingAvailableOnScreening: "isVodPricingAvailableOnScreening",
    }),
    isScreeningArchive: function(){
      return isDatePast(this.screener.registration_ends_at);
    },
    ...mapState({
      currentCompany: (state) => state.company.company,
    }),
  },
  methods: {
    checkPurchasingDateIsFuture(end_date) {
      return isDateFuture(end_date);
    },
    reset(){
      if(this.$refs.pricingPlanForm){
        this.$refs.pricingPlanForm.reset();
      }
    }
  }
};
</script>

<style>
</style>
